// The dashboard's filter panel: its state, its defaults, and how its task filters narrow the task
// rows. One state for the task cards, the calendar and the trials table, saved per user
// (settings: dashboard_filters).
import { EMPTY_TRIAL_FILTERS, activeFilters } from './trialSearchFilters';
import { getTaskBadge } from './taskBadge';

export const DEFAULT_DASHBOARD_FILTERS = {
  ...EMPTY_TRIAL_FILTERS,
  statuses: [], // task status keys (utils/taskStatus.js); none = all
  type: '', // va_id: '1' visit, '2' application, '8' activity; '' = all
  assignee: '', // a user id, 'unassigned', or '' = anyone
  mineOnly: true, // only the user's trials and tasks (SD, PI, FT or assigned)
  hideOldOverdue: false, // hide tasks overdue for more than 6 months (off by default: customer feedback 2)
};

// 'none' is "No date" (empty, ? or TBD): a choice like the others, not a way to hide them
export const TASK_FILTER_STATUSES = ['overdue', 'soon', 'week', 'later', 'text', 'none'];

export const TASK_TYPES = [
  { value: '1', label: 'Visit' },
  { value: '2', label: 'Application' },
  { value: '8', label: 'Activity' },
];

/** Saved filters (or none) on top of the defaults, keeping only known keys. */
export function dashboardFiltersFrom(saved) {
  const filters = { ...DEFAULT_DASHBOARD_FILTERS };
  if (saved && typeof saved === 'object') {
    for (const key of Object.keys(DEFAULT_DASHBOARD_FILTERS)) {
      if (key in saved) filters[key] = saved[key];
    }
  }
  if (!Array.isArray(filters.statuses)) filters.statuses = [];
  return filters;
}

/** The trial filters to send to the API (only those set). */
export function trialFiltersOf(filters) {
  return activeFilters(filters);
}

/** Whether a task filter that also narrows the trials table is set (status, type or assignee). */
export function hasTaskFilters(filters) {
  return filters.statuses.length > 0 || filters.type !== '' || filters.assignee !== '';
}

/** Whether anything differs from the defaults (for "Reset filters"). */
export function differsFromDefaults(filters) {
  return JSON.stringify(dashboardFiltersFrom(filters)) !== JSON.stringify(DEFAULT_DASHBOARD_FILTERS);
}

const SIX_MONTHS_AGO = (today) => new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());

/**
 * Whether a task is the user's: assigned to them, or on a trial where they are SD, PI or FT. (The API's
 * "mine" scope returns whole trials - also one where the user is assigned to just one task.)
 */
export function isMyTask(trial, task, userId) {
  if (userId == null) return true;
  const me = String(userId);
  if ((Array.isArray(task?.assignees) ? task.assignees : []).some((a) => String(a?.id) === me)) return true;
  return [trial?.studydirector, trial?.PI, trial?.FT].some((id) => id != null && String(id) === me);
}

/** Whether an enriched task (of `trial`) passes the task filters, for the user `userId`. */
export function taskPasses(task, filters, today = new Date(), trial = null, userId = null) {
  if (!task) return false;
  if (filters.mineOnly && trial && !isMyTask(trial, task, userId)) return false;
  if (filters.type !== '' && String(task.va_id) !== String(filters.type)) return false;
  if (filters.assignee !== '') {
    const assignees = Array.isArray(task.assignees) ? task.assignees : [];
    if (filters.assignee === 'unassigned' ? assignees.length > 0 : !assignees.some((a) => String(a?.id) === String(filters.assignee))) return false;
  }
  const status = getTaskBadge(task, today).status;
  if (filters.statuses.length > 0 && !filters.statuses.includes(status)) return false;
  if (filters.hideOldOverdue && status === 'overdue' && task.dueDate && new Date(task.dueDate) < SIX_MONTHS_AGO(today)) return false;
  return true;
}
